import wsMessageTypes from 'constants/wsMessageTypes';
import { MessageOwner } from 'types';
import { CANNED_REPLIES, INITIAL_MESSAGES } from './demoScript';

export interface ResponderContext {
  sessionId: string;
  nextSequenceId: () => number;
  replyCursor: () => number;
  hasSeededInitialMessages: () => boolean;
  markInitialMessagesSeeded: () => void;
}

export interface DelayedReply {
  delayMs: number;
  payload: Record<string, any>;
}

export const createResponderContext = (): ResponderContext => {
  let sequence = 0;
  let replyIndex = 0;
  let initialSeeded = false;
  return {
    sessionId: `preview-${crypto
      .randomUUID()
      .replace(/-/g, '')
      .slice(0, 8)}`,
    nextSequenceId: () => ++sequence,
    replyCursor: () => replyIndex++,
    hasSeededInitialMessages: () => initialSeeded,
    markInitialMessagesSeeded: () => {
      initialSeeded = true;
    },
  };
};

export const handleClientMessage = (
  raw: string,
  ctx: ResponderContext,
): DelayedReply[] => {
  let msg: any;
  try {
    msg = JSON.parse(raw);
  } catch {
    return [];
  }

  switch (msg.type) {
    case wsMessageTypes.START_SESSION_REQUEST:
    case wsMessageTypes.SESSION_RESUME_REQUEST: {
      const dialogId = `preview-dialog-${ctx.sessionId}`;
      const replies: DelayedReply[] = [
        {
          delayMs: 100,
          payload: {
            type: wsMessageTypes.START_SESSION_RESPONSE,
            session_id: ctx.sessionId,
            dialog_id: dialogId,
          },
        },
      ];

      if (ctx.hasSeededInitialMessages()) {
        return replies;
      }
      ctx.markInitialMessagesSeeded();

      INITIAL_MESSAGES.forEach((message, index) => {
        const basePayload: Record<string, any> = {
          type: wsMessageTypes.DIALOG_MESSAGE_EVENT,
          dialog_id: dialogId,
          source: message.source,
          sequence_id: ctx.nextSequenceId() + 1,
        };

        if (message.source === MessageOwner.USER) {
          basePayload.client_message_id = `preview-initial-${index}`;
          basePayload.utterance = message.content;
        } else {
          basePayload.dialog_response = {
            prompt: { content: message.content },
          };
        }

        replies.push({
          delayMs: 400 + index * 200,
          payload: basePayload,
        });
      });

      return replies;
    }

    case wsMessageTypes.DIALOG_REQUEST:
    case wsMessageTypes.START_DIALOG_REQUEST: {
      const replyText =
        CANNED_REPLIES[ctx.replyCursor() % CANNED_REPLIES.length];
      const ackSequence = ctx.nextSequenceId() + 1;
      const botSequence = ackSequence + 1;

      return [
        {
          delayMs: 120,
          payload: {
            type: wsMessageTypes.DIALOG_MESSAGE_EVENT,
            dialog_id: `preview-dialog-${ctx.sessionId}`,
            source: MessageOwner.USER,
            sequence_id: ackSequence,
            client_message_id: msg.client_message_id,
            utterance: msg.utterance,
          },
        },
        {
          delayMs: 250,
          payload: {
            type: wsMessageTypes.TYPING_EVENT,
            typing_on: true,
          },
        },
        {
          delayMs: 900,
          payload: {
            type: wsMessageTypes.DIALOG_MESSAGE_EVENT,
            dialog_id: `preview-dialog-${ctx.sessionId}`,
            source: MessageOwner.BOT,
            sequence_id: botSequence,
            dialog_response: {
              prompt: { content: replyText },
            },
          },
        },
      ];
    }

    default:
      return [];
  }
};
